import { Copy, Bot, Wallet, Clock, PenTool, BadgeCheck, Fingerprint, X, ArrowRight } from "lucide-react";
import { Chapter, Reveal } from "./Shared";
import { scrollToId } from "@/lib/scroll";

const PAINS = [
  "Hours spent generating files",
  "Fixing AI mistakes",
  "Removing backgrounds",
  "Searching for graphics",
  "Organising exports",
];

const GUARANTEES = [
  { icon: Clock, main: "24-Hour Delivery Guarantee", test: "guarantee-badge-delivery" },
  { icon: PenTool, main: "Human-Crafted Vector Files — Zero AI Generation", test: "guarantee-badge-human" },
  { icon: BadgeCheck, main: "Commercial License on Every Design", test: "guarantee-badge-license" },
  { icon: Fingerprint, main: "Exclusive to You — Never Shared, Never Resold", test: "guarantee-badge-exclusive" },
];

export default function Problem() {
  return (
    <section id="problem" data-testid="problem-section-container" className="relative">
      <div className="mx-auto grid max-w-6xl items-start gap-14 px-6 py-16 sm:py-20 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <Reveal>
            <Chapter num="01" label="The Growth Bottleneck" />
            <h2 className="mt-6 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
              Your shop doesn't need another design tool. It needs someone to actually make the
              designs.
            </h2>
          </Reveal>

          <Reveal delay={0.12}>
            <ul data-testid="problem-pain-list" className="mt-9 space-y-4">
              {PAINS.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 rounded-2xl border border-white/10 bg-[#0C1222]/70 px-5 py-4 text-[15px] text-slate-300"
                >
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-rose-400/30 bg-rose-500/10">
                    <X className="h-3 w-3 text-rose-400" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="mt-6 text-sm leading-relaxed text-slate-500">
              More designs means more products to test and more chances to find your next winner.
            </p>
            <button
              data-testid="problem-bold-line"
              onClick={() => scrollToId("#design-request")}
              className="group mt-6 flex items-center gap-2.5 text-left text-lg font-bold text-white transition-colors hover:text-amber-300 sm:text-xl"
            >
              What if you could just send the idea?
              <ArrowRight className="h-5 w-5 text-amber-400 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:sticky lg:top-28">
          <div className="frame-spotlight relative overflow-hidden rounded-3xl border border-white/10 bg-[#0C1222]/80 p-6 sm:p-7">
            <span className="absolute left-4 top-4 z-10 h-5 w-5 border-l-2 border-t-2 border-amber-400/80" />
            <span className="absolute bottom-4 right-4 z-10 h-5 w-5 border-b-2 border-r-2 border-amber-400/80" />

            <p className="relative z-10 text-sm font-bold leading-snug text-white">
              The Overnight Design Infrastructure for Scalable Print-on-Demand Stores
            </p>

            <div data-testid="guarantee-badges" className="relative z-10 mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {GUARANTEES.map((g) => (
                <div
                  key={g.test}
                  data-testid={g.test}
                  className="flex items-center gap-3 rounded-xl border border-amber-400/25 bg-[#0A1020]/80 px-4 py-3"
                >
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-amber-400/30 bg-amber-400/10">
                    <g.icon className="h-4 w-4 text-amber-300" />
                  </span>
                  <p className="min-w-0 text-[13px] font-semibold leading-snug text-slate-200">{g.main}</p>
                </div>
              ))}
            </div>

            <p className="relative z-10 mt-6 border-t border-white/5 pt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
              Every order. Every time. No exceptions.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}